(function () {
  if (customElements.get('rbc-grind-fineness') !== undefined) {
    return;
  }

  var LEGEND = { 1: 'Coarse', 2: 'French Press', 3: 'French Press+', 4: 'Drip', 5: 'Cone / Pour-over', 6: 'Fine Drip', 7: 'Espresso', 8: 'Espresso+', 9: 'Extra Fine', 10: 'Turkish' };

  // RBC: must extend HTMLParsedElement, not plain HTMLElement, and must be
  // an external deferred script (not inline). This snippet renders once per
  // product, so pages like the homepage Best Sellers grid have several
  // <rbc-grind-fineness> instances. Once the FIRST instance's script calls
  // customElements.define(), every later instance already in the token
  // stream gets upgraded (connectedCallback fires) the instant its opening
  // tag is parsed — before the parser has read its own children (overlay,
  // buttons, radios). Plain HTMLElement made those querySelector() lookups
  // resolve to null for every card after the first, throwing in
  // connectedCallback and silently breaking the popup. parsedCallback
  // defers until this element's children are actually in the DOM — the
  // same fix already used by ce-product-quantity/ce-ticker/etc. elsewhere
  // in this theme for the identical multi-instance problem. HTMLParsedElement
  // itself lives in vendors.js, loaded deferred in <head> — an inline
  // <script> in the snippet runs synchronously as the parser reaches it,
  // before that deferred script has executed, so HTMLParsedElement isn't
  // defined yet. Loading this file the same deferred way (in document
  // order, after vendors.js) guarantees it's available by the time this runs.
  class RbcGrindFineness extends HTMLParsedElement {
    parsedCallback() {
      this.input = this.querySelector('[data-rbc-grind-fineness-input]');
      this.summary = this.querySelector('[data-rbc-grind-fineness-summary]');
      this.overlay = this.querySelector('[data-rbc-grind-fineness-overlay]');
      this.closeBtn = this.querySelector('[data-rbc-grind-fineness-close]');
      this.saveBtn = this.querySelector('[data-rbc-grind-fineness-save]');
      this.requiredNotice = this.querySelector('[data-rbc-grind-fineness-required-notice]');
      this.radios = Array.from(this.querySelectorAll('[data-rbc-grind-fineness-radio]'));

      // RBC: a customer could pick "Ground", have the popup open, then
      // close it (× button, click outside, Esc) without ever clicking
      // Save — the order would then go through as "Ground" with no
      // fineness number, which is useless to whoever grinds it. Track
      // whether Ground is the live selection so add-to-cart can be
      // gated on a real saved value existing.
      this.isGroundSelected = false;

      this._onGroundChange = this._onGroundChange.bind(this);
      this._open = this._open.bind(this);
      this._close = this._close.bind(this);
      this._save = this._save.bind(this);
      this._guardSubmit = this._guardSubmit.bind(this);

      // ce-radio-group dispatches "ce-radio-group:change" both on genuine
      // user clicks AND (sometimes more than once) during its own init,
      // to sync the page-load default selection — identical event shape,
      // no timing gap to key off. So we track the last known value per
      // option position and only react when it actually *changes*;
      // repeated init-time echoes of the same value are no-ops.
      this._lastValueByPosition = {};

      // Scope listening to this component's own product card/form —
      // when several products render their own <rbc-grind-fineness> on
      // the same page (homepage best-sellers grid), a document-wide
      // listener would make every card's popup react to every OTHER
      // card's variant change.
      this._scope = this.closest('rbc-purchase-card, ce-product') || document;

      // The theme's pills-style variant picker (ce-radio-group) dispatches
      // this custom event on selection — it does not use native <input>
      // radios, so a plain 'change' listener would never fire.
      this._scope.addEventListener('ce-radio-group:change', this._onGroundChange);
      // Native <select> (drop-down display mode) still uses real 'change'.
      this._scope.addEventListener('change', this._onGroundChange);
      this.closeBtn.addEventListener('click', this._close);
      this.saveBtn.addEventListener('click', this._save);

      // On the PDP this._scope is <ce-product>, an ancestor of the real
      // <form> the buy button submits — a capture-phase listener on an
      // ancestor always runs before any listener on the form itself
      // (native.js's own submit handling included), regardless of which
      // script happened to load/register first. This is what actually
      // blocks the native "Add to cart" button, not just our own UI.
      this._scope.addEventListener('submit', this._guardSubmit, true);
    }

    _onGroundChange(event) {
      var value = event.detail && event.detail.value !== undefined ? event.detail.value : event.target && event.target.value;
      if (value === undefined) {
        return;
      }

      // Radio-group (pills) events carry a stable position index we key
      // the last-known-value tracking on. Native <select> 'change'
      // events have no such index (position null) — always treat those
      // as real since there's no init-sync equivalent for native selects.
      var position = event.detail ? event.detail.index : '__select__';
      var isFirstSighting = !(position in this._lastValueByPosition);
      var valueChanged = this._lastValueByPosition[position] !== value;
      this._lastValueByPosition[position] = value;

      if (position !== '__select__' && (isFirstSighting || !valueChanged)) {
        // First-ever dispatch for this option, or a repeated echo of the
        // same value — both are init noise, not a user picking a value.
        return;
      }

      if (value === 'Ground') {
        this.isGroundSelected = true;
        this._open();
      } else if (value === 'Whole Bean' || value === 'Espresso Grind') {
        this.isGroundSelected = false;
        this.input.value = '';
        this.summary.hidden = true;
      }
    }

    // Public: used by the homepage purchase card (rbc-best-sellers.liquid),
    // which adds to cart via its own fetch() call rather than a native
    // form submit, so it can't rely on the submit-capture guard below.
    hasUnsavedGroundSelection() {
      return this.isGroundSelected && !this.input.value;
    }

    _guardSubmit(event) {
      if (!this.hasUnsavedGroundSelection()) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.requiredNotice.hidden = false;
      this._open();
      this.overlay.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    _open() {
      this.overlay.hidden = false;
    }

    _close() {
      this.overlay.hidden = true;
    }

    _save() {
      var checked = this.radios.find(function (r) { return r.checked; });
      var value = checked ? checked.value : '5';
      var label = LEGEND[value] || '';
      this.input.value = value + (label ? ' (' + label + ')' : '');
      this.summary.textContent = 'Grind fineness: ' + value + (label ? ' — ' + label : '');
      this.summary.hidden = false;
      this.requiredNotice.hidden = true;
      this._close();
    }
  }

  customElements.define('rbc-grind-fineness', RbcGrindFineness);
})();
